import React, { useState, useMemo, useCallback, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Coins, Copy, ShoppingCart, Loader2, CheckCircle2, AlertCircle, FileText,
} from "lucide-react";
import { formatLKR, apiGet } from "@/lib/api";
import { CompanyLink, SymbolLink } from "@/components/common/StockLinks";
import SignalBadge, { ScoreText } from "@/components/common/SignalBadge";
import DataConfidenceDot from "@/components/common/DataConfidenceDot";
import SectorVerdictPill from "@/components/common/SectorVerdictPill";
import { apiPost } from "@/lib/api";
import { toast } from "sonner";

/**
 * BulkPurchaseFlow — Phase 7 Stage 9 Part A.
 *
 * Reusable checkbox-based selection panel used by:
 *   1. PortfolioSuggestion (after creation) — props.allocations from sug
 *   2. MonthlyInvestmentDialog — props.allocations from monthly-suggest
 *
 * Each allocation must contain:
 *   { cse_symbol, company_code, company_name, sector, score?, shares,
 *     close_price, allocation_lkr, expected_annual_dividend_lkr, ... }
 *
 * The component:
 *   - lets users tick/untick stocks; reallocates LKR across selected
 *     stocks instantly (proportional to score, fallback equal-weight)
 *   - shows a sticky summary panel at the bottom
 *   - has a "Copy buy list for broker" button
 *   - has a "Record all purchases" button that opens a confirmation
 *     modal where each price is editable
 */
const BulkPurchaseFlow = ({
  portfolioId,
  portfolioName,
  totalAmount,
  allocations,
  onCompleted,
  testIdPrefix = "bulk-purchase",
  ctaLabel = "Record all purchases",
  // Phase 4 (Item 17) — Allow callers to brand the back-button. When the
  // user opens a stock from a Monthly Investment dialog they should see
  // "← Back to Monthly Investment" on the Stock Detail page, not the
  // generic "Portfolio" pointer.
  fromLabel = "Portfolio",
}) => {
  // Phase 4 (Item 14) — Preserve checkbox selection across navigation.
  // When the user clicks a stock to view its detail page and comes back,
  // their tick/untick choices must NOT reset. We persist the set of
  // selected cse_symbols in sessionStorage, keyed by portfolio + flow.
  const persistKey = `bulk-purchase:${testIdPrefix}:${portfolioId || "anon"}`;
  const [selected, setSelected] = useState(() => {
    try {
      const raw = sessionStorage.getItem(persistKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed) && parsed.length) {
          // Filter against the current allocation list so we never end up
          // with stale symbols that are no longer suggested.
          const validSyms = new Set(allocations.map((a) => a.cse_symbol));
          const restored = parsed.filter((s) => validSyms.has(s));
          if (restored.length) return new Set(restored);
        }
      }
    } catch (_e) { /* ignore */ }
    return new Set(allocations.map((a) => a.cse_symbol));
  });
  // Persist whenever selection changes
  useEffect(() => {
    try {
      sessionStorage.setItem(persistKey, JSON.stringify(Array.from(selected)));
    } catch (_e) { /* ignore quota errors */ }
  }, [persistKey, selected]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  // Stage 1 — brokerage rates
  const [rates, setRates] = useState({ buy_rate: 0.0121, sell_rate: 0.0121 });
  useEffect(() => {
    apiGet("/portfolio-settings").then(setRates).catch(() => {});
  }, []);

  const toggle = useCallback((sym) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(sym)) next.delete(sym);
      else next.add(sym);
      return next;
    });
  }, []);

  // Redistribute the LKR across selected stocks proportional to score
  // (fallback: equal weight). Recalculate share counts using close_price.
  const adjusted = useMemo(() => {
    const picks = allocations.filter((a) => selected.has(a.cse_symbol));
    if (picks.length === 0) return [];
    const scoreSum = picks.reduce((s, a) => s + (a.score || 50), 0) || 1;
    return picks.map((a) => {
      const weight = (a.score || 50) / scoreSum;
      const lkr = totalAmount * weight;
      const price = Number(a.close_price) || 0;
      const shares = price > 0 ? Math.floor(lkr / price) : 0;
      const actualLkr = shares * price;
      const dpsRate =
        a.expected_annual_dividend_lkr && a.shares
          ? a.expected_annual_dividend_lkr / a.shares
          : 0;
      return {
        ...a,
        shares,
        allocation_lkr: actualLkr,
        allocation_pct: (weight * 100).toFixed(1),
        expected_annual_dividend_lkr: shares * dpsRate,
      };
    });
  }, [allocations, selected, totalAmount]);

  const summary = useMemo(() => {
    const used = adjusted.reduce((s, a) => s + a.allocation_lkr, 0);
    const dividends = adjusted.reduce(
      (s, a) => s + (a.expected_annual_dividend_lkr || 0),
      0
    );
    const brokerage = used * (rates.buy_rate || 0);
    return {
      used,
      cash: Math.max(0, totalAmount - used),
      dividends,
      yield_pct: totalAmount > 0 ? (dividends / totalAmount) * 100 : 0,
      brokerage,
      effective_invested: used + brokerage, // what user actually pays
    };
  }, [adjusted, totalAmount, rates.buy_rate]);

  const copyForBroker = async () => {
    const date = new Date().toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
    const lines = [
      `CSE Purchase Order — ${date}`,
      `Portfolio: ${portfolioName || "My CSE Portfolio"}`,
      "",
    ];
    adjusted.forEach((a, i) => {
      const tradeBrokerage = (a.allocation_lkr || 0) * (rates.buy_rate || 0);
      lines.push(`${i + 1}. ${a.cse_symbol} (${a.company_name || ""})`);
      lines.push(`   Buy: ${a.shares.toLocaleString()} shares`);
      lines.push(`   At market price approx Rs.${Number(a.close_price).toFixed(2)}`);
      lines.push(`   Brokerage est: Rs.${tradeBrokerage.toLocaleString(undefined, { maximumFractionDigits: 0 })}`);
      lines.push("");
    });
    lines.push(
      `Total investment: Rs.${summary.used.toLocaleString(undefined, {
        maximumFractionDigits: 2,
      })}`
    );
    lines.push(
      `Total brokerage (${(rates.buy_rate * 100).toFixed(2)}%): Rs.${summary.brokerage.toLocaleString(undefined, { maximumFractionDigits: 0 })}`
    );
    lines.push(
      `Total cost incl. brokerage: Rs.${summary.effective_invested.toLocaleString(undefined, { maximumFractionDigits: 2 })}`
    );
    lines.push("");
    lines.push("Note: 10% withholding tax will be deducted from dividend payments at source.");
    lines.push("Generated by CSE Investor app");
    try {
      await navigator.clipboard.writeText(lines.join("\n"));
      toast.success("Buy list copied to clipboard", {
        description: "Paste into your broker's order form.",
      });
    } catch (e) {
      toast.error("Could not copy — please select & copy manually");
    }
  };

  return (
    <Card className="border bg-card" data-testid={testIdPrefix}>
      <CardHeader className="pb-3">
        <CardTitle className="font-display text-base flex items-center gap-2">
          <ShoppingCart className="h-4 w-4 text-[hsl(var(--primary))]" />
          Select stocks to purchase
        </CardTitle>
        <CardDescription>
          Tick or untick stocks below. The remaining LKR auto-redistributes
          proportional to the composite score across selected stocks.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {allocations.map((a) => {
          const isSelected = selected.has(a.cse_symbol);
          const adj = adjusted.find((x) => x.cse_symbol === a.cse_symbol);
          return (
            <div
              key={a.cse_symbol}
              className={`rounded-md border p-3 transition-opacity ${
                isSelected ? "bg-background" : "bg-muted/30 opacity-70"
              }`}
              data-testid={`${testIdPrefix}-row-${a.cse_symbol}`}
            >
              <div className="flex items-start gap-3">
                <Checkbox
                  checked={isSelected}
                  onCheckedChange={() => toggle(a.cse_symbol)}
                  className="mt-1"
                  id={`${testIdPrefix}-cb-${a.cse_symbol}`}
                  data-testid={`${testIdPrefix}-cb-${a.cse_symbol}`}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <CompanyLink
                      companyCode={a.company_code}
                      name={a.company_name}
                      fromLabel={fromLabel}
                      className="font-medium"
                    />
                    <SymbolLink
                      companyCode={a.company_code}
                      symbol={a.cse_symbol}
                      fromLabel={fromLabel}
                      className="text-xs text-muted-foreground"
                    />
                    {a.sector && (
                      <Badge variant="secondary" className="text-[10px]">
                        {a.sector}
                      </Badge>
                    )}
                    {/* Phase 9 (Critical Bug 2 + colour fix) — Signal badge
                        with bold screener-grade colours. */}
                    {a.composite_signal && (
                      <SignalBadge
                        signal={a.composite_signal}
                        size="sm"
                        className=""
                      />
                    )}
                    {(a.composite_score ?? a.score) != null && (
                      <ScoreText
                        score={a.composite_score ?? a.score}
                        className="text-[11px]"
                      />
                    )}
                    {/* Phase 9 — Data confidence dot (popover variant). */}
                    {a.data_confidence && (
                      <DataConfidenceDot
                        level={a.data_confidence}
                        annualReports={a.annual_reports || 0}
                        interimReports={a.interim_reports || 0}
                        companyCode={a.company_code}
                        size="sm"
                        testId={`${testIdPrefix}-conf-${a.cse_symbol}`}
                      />
                    )}
                    {/* Phase 9 — Sector verdict coloured pill. */}
                    {a.sector_verdict && (
                      <SectorVerdictPill
                        verdict={a.sector_verdict}
                        size="sm"
                        testId={`${testIdPrefix}-verdict-${a.cse_symbol}`}
                      />
                    )}
                    {/* Phase 9 (Step 8) — show stale-signal warning if the
                        stock dropped from BUY since suggestion was generated. */}
                    {a.signal_changed && (
                      <Badge
                        variant="outline"
                        className="text-[10px] bg-[hsl(var(--destructive))]/10 text-[hsl(var(--destructive))] border-[hsl(var(--destructive))]/40"
                        data-testid={`${testIdPrefix}-stale-${a.cse_symbol}`}
                        title={`Now ${a.current_composite_signal || "?"} (${Math.round(a.current_composite_score || 0)}/100)`}
                      >
                        ⚠ Signal changed
                      </Badge>
                    )}
                  </div>
                  {a.sector_verdict === "UNDERWEIGHT" && (
                    <p className="mt-1 text-[11px] text-[hsl(var(--warning))] flex items-center gap-1">
                      <span>⚠</span>
                      This sector currently has unfavourable macro conditions.
                      Consider a smaller allocation.
                    </p>
                  )}
                  {/* Test 4 Fix 4 / Test 6 Fix 1 — Canonical weighted base
                      case target (PE 40% + PB 40% + DDM 20%) so the number
                      here matches the screener and stock detail headline.
                      Falls back to the simple 4-method average for legacy
                      records that haven't been recomputed. */}
                  {(() => {
                    const tgt =
                      a.target_base_case && a.target_base_case > 0
                        ? a.target_base_case
                        : a.target_average && a.target_average > 0
                          ? a.target_average
                          : null;
                    const upside =
                      a.target_base_case_upside_pct != null
                        ? a.target_base_case_upside_pct
                        : tgt != null && a.close_price && a.close_price > 0
                          ? ((tgt - a.close_price) / a.close_price) * 100
                          : null;
                    if (tgt == null || upside == null) return null;
                    const isUp = upside > 0;
                    return (
                      <div
                        className="mt-1.5 inline-flex items-center gap-1.5 text-[11px] tabular-nums"
                        data-testid={`${testIdPrefix}-target-${a.cse_symbol}`}
                      >
                        <span className="text-muted-foreground">🎯 Target:</span>
                        <span className="font-display font-semibold text-foreground">
                          {formatLKR(tgt, { decimals: 2 })}
                        </span>
                        <span
                          className={`font-medium ${
                            isUp
                              ? "text-[hsl(var(--success))]"
                              : upside < 0
                                ? "text-[hsl(var(--destructive))]"
                                : "text-muted-foreground"
                          }`}
                        >
                          ({isUp ? "+" : ""}
                          {upside.toFixed(1)}% {isUp ? "upside" : "downside"})
                        </span>
                      </div>
                    );
                  })()}
                  {/* Phase 7 (Deep Analysis) — Macro / Geo verdict chips */}
                  {(a.macro_verdict || a.geo_verdict) && (
                    <div
                      className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px]"
                      data-testid={`${testIdPrefix}-macrogeo-${a.cse_symbol}`}
                    >
                      {a.macro_verdict && (
                        <span className="inline-flex items-center gap-1 text-muted-foreground">
                          <span>🏦 Macro:</span>
                          <span className="font-medium text-foreground capitalize">
                            {a.macro_verdict.toLowerCase()}
                          </span>
                          <span>{a.macro_emoji || ""}</span>
                        </span>
                      )}
                      {a.geo_verdict && (
                        <span className="inline-flex items-center gap-1 text-muted-foreground">
                          <span>🌍 Geo:</span>
                          <span className="font-medium text-foreground capitalize">
                            {a.geo_verdict.toLowerCase()}
                          </span>
                          <span>{a.geo_emoji || ""}</span>
                        </span>
                      )}
                    </div>
                  )}
                  <div className="mt-1.5 grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                    <Cell label="Allocated">
                      {formatLKR(adj?.allocation_lkr ?? a.allocation_lkr, {
                        compact: true,
                        decimals: 1,
                      })}
                      {adj && (
                        <span className="text-muted-foreground ml-1">
                          ({adj.allocation_pct}%)
                        </span>
                      )}
                    </Cell>
                    <Cell label="Shares">
                      {(adj?.shares ?? a.shares).toLocaleString()}
                    </Cell>
                    <Cell label="Price">
                      {formatLKR(a.close_price, { decimals: 2 })}
                    </Cell>
                    <Cell label="Est. div/yr (net)">
                      <span className="text-[hsl(var(--success))]" title="After 10% withholding tax — what you actually receive">
                        {formatLKR(adj?.expected_annual_dividend_lkr ?? 0, {
                          compact: true,
                          decimals: 1,
                        })}
                      </span>
                    </Cell>
                  </div>
                  {/* Phase 9 fix — `reason` text removed; the same info
                      (score, signal, sector verdict) is already shown
                      visually as bold colour-coded badges above. */}
                </div>
              </div>
            </div>
          );
        })}

        {/* Summary panel — sticky at bottom */}
        <div
          className="rounded-md border-2 border-primary/30 bg-primary/5 p-4 space-y-2 sticky bottom-2"
          data-testid={`${testIdPrefix}-summary`}
        >
          <div className="text-sm font-display font-semibold flex items-center gap-1.5">
            <CheckCircle2 className="h-4 w-4 text-[hsl(var(--success))]" />
            {adjusted.length} of {allocations.length} stocks selected
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
            <Cell label="Allocated">
              {formatLKR(summary.used, { compact: true, decimals: 1 })}
            </Cell>
            <Cell label="Cash remainder">
              {formatLKR(summary.cash, { compact: true, decimals: 1 })}
            </Cell>
            <Cell label={`Brokerage (${(rates.buy_rate * 100).toFixed(2)}%)`}>
              <span className="text-[hsl(var(--destructive))]">
                {formatLKR(summary.brokerage, { compact: true, decimals: 1 })}
              </span>
            </Cell>
            <Cell label="Total cost">
              {formatLKR(summary.effective_invested, { compact: true, decimals: 1 })}
            </Cell>
            <Cell label="Total budget">
              {formatLKR(totalAmount, { compact: true, decimals: 1 })}
            </Cell>
            <Cell label="Est. yield (net of 10% WHT)">
              <span className="text-[hsl(var(--success))]">
                {formatLKR(summary.dividends, { compact: true, decimals: 1 })}/yr ·{" "}
                {summary.yield_pct.toFixed(2)}%
              </span>
            </Cell>
          </div>
          <div className="flex flex-wrap items-center gap-2 pt-1">
            <Button
              variant="outline"
              size="sm"
              onClick={copyForBroker}
              disabled={adjusted.length === 0}
              className="gap-1.5"
              data-testid={`${testIdPrefix}-copy-broker`}
            >
              <Copy className="h-3.5 w-3.5" /> 📋 Copy buy list for broker
            </Button>
            <Button
              size="sm"
              onClick={() => setConfirmOpen(true)}
              disabled={adjusted.length === 0}
              className="gap-1.5"
              data-testid={`${testIdPrefix}-record`}
            >
              <ShoppingCart className="h-3.5 w-3.5" /> ✅ {ctaLabel}
            </Button>
          </div>
        </div>
      </CardContent>

      <ConfirmPurchaseDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        portfolioId={portfolioId}
        rows={adjusted}
        onCompleted={(res) => {
          setConfirmOpen(false);
          onCompleted?.(res);
        }}
      />
    </Card>
  );
};

const Cell = ({ label, children }) => (
  <div>
    <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
    <div className="font-medium tabular">{children}</div>
  </div>
);

const ConfirmPurchaseDialog = ({ open, onClose, portfolioId, rows, onCompleted }) => {
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);
  const [prices, setPrices] = useState(() => {
    const m = {};
    rows.forEach((r) => (m[r.cse_symbol] = String(Number(r.close_price).toFixed(2))));
    return m;
  });
  const [busy, setBusy] = useState(false);

  React.useEffect(() => {
    if (open) {
      const m = {};
      rows.forEach((r) => (m[r.cse_symbol] = String(Number(r.close_price).toFixed(2))));
      setPrices(m);
      setDate(today);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const submit = async () => {
    setBusy(true);
    try {
      const items = rows.map((r) => ({
        cse_symbol: r.cse_symbol,
        shares_purchased: r.shares,
        purchase_price: parseFloat(prices[r.cse_symbol]) || r.close_price,
        purchase_date: date,
      }));
      const res = await apiPost(
        `/portfolios/${portfolioId}/holdings/bulk`,
        items,
      );
      if (res.failed > 0) {
        toast.warning(
          `${res.succeeded} added · ${res.failed} failed`,
          {
            description: (res.results || [])
              .filter((r) => !r.ok)
              .map((r) => `${r.cse_symbol}: ${r.error}`)
              .join("; "),
          },
        );
      } else {
        toast.success(`✅ ${res.succeeded} holdings added`, {
          description: "Open the dashboard to view them.",
        });
      }
      onCompleted?.(res);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not record purchases");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="sm:max-w-lg" data-testid="confirm-purchase-dialog">
        <DialogHeader>
          <DialogTitle>Confirm all purchases</DialogTitle>
          <DialogDescription>
            Edit the price you actually paid for each stock. Pre-filled with the latest close price.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 max-h-[50vh] overflow-y-auto">
          {rows.map((r) => (
            <div
              key={r.cse_symbol}
              className="grid grid-cols-[1fr_120px] items-center gap-3 rounded-md border p-2"
              data-testid={`confirm-row-${r.cse_symbol}`}
            >
              <div>
                <div className="text-sm font-medium">{r.cse_symbol}</div>
                <div className="text-xs text-muted-foreground">
                  {r.company_name} · {r.shares.toLocaleString()} shares
                </div>
              </div>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={prices[r.cse_symbol] ?? ""}
                onChange={(e) =>
                  setPrices((p) => ({ ...p, [r.cse_symbol]: e.target.value }))
                }
                className="h-9 text-right tabular"
                data-testid={`confirm-price-${r.cse_symbol}`}
              />
            </div>
          ))}
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="purchase-date" className="text-xs">
            Purchase date
          </Label>
          <Input
            id="purchase-date"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            data-testid="confirm-date"
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={busy} data-testid="confirm-add-all">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-2" /> : null}
            Add All Holdings
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BulkPurchaseFlow;
